import { useState, useEffect, useMemo } from 'react';
import { useNewsStore, CalendarEvent } from '../store/newsStore';

export interface NewsProximityEvent {
  id: string;
  event: string;
  country: string;
  timestamp: number;
  minutesUntil: number;
  importance: 'high';
  category?: 'economic' | 'earnings';
  ticker?: string;
}

export interface NewsProximityState {
  nearbyEvents: NewsProximityEvent[];
  isNearEvent: boolean;
  closestEvent: NewsProximityEvent | null;
  minutesUntilClosest: number | null;
}

const TEN_MINUTES_MS = 10 * 60 * 1000;
/**
 * Countries whose releases can actually breach the account.
 *
 * The prop-firm rule is "high-impact USD news" — a UK GDP print cannot breach a
 * USD-rule funded account, so warning about one is pure noise. Before this
 * filter existed a single UK release fired four separate toasts (GDP 3M/3M,
 * GDP MoM, GDP YoY, Manufacturing Production) at two thresholds each: eight
 * warnings, none of them actionable, stacked over the chart.
 *
 * A list rather than a bare `=== 'US'` because the rule is per prop firm, and
 * a firm that also restricts EUR releases should be one entry away.
 */
export const BREACH_RELEVANT_COUNTRIES = ['US'];

/**
 * Whether an event is worth warning about.
 *
 * Country is checked HERE and not in the calendar's restriction windows: the
 * chart shading is a map of the session's volatility, which a UK print is
 * genuinely part of, whereas a "CLOSE YOUR TRADE" toast is a call to action and
 * a call to action about something that cannot hurt you is just training you to
 * dismiss toasts.
 */
export function isBreachRelevant(e: CalendarEvent): boolean {
  if (e.importance !== 'high') return false;
  // Earnings carry no country and are opt-in elsewhere; they are not a
  // red-folder macro release and are left alone by this filter.
  if (e.category === 'earnings') return true;
  return BREACH_RELEVANT_COUNTRIES.includes(e.country);
}

const POLL_INTERVAL_MS = 15_000;

export function useNewsProximity(): NewsProximityState {
  const calendarEvents = useNewsStore((state) => state.calendarEvents);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const nearbyEvents = useMemo(() => {
    return calendarEvents
      .filter((e: CalendarEvent) => {
        if (!isBreachRelevant(e)) return false;
        const diff = e.timestamp - now;
        return diff > 0 && diff <= TEN_MINUTES_MS;
      })
      .map((e: CalendarEvent) => ({
        id: e.id,
        event: e.event,
        country: e.country,
        timestamp: e.timestamp,
        minutesUntil: Math.ceil((e.timestamp - now) / 60_000),
        importance: 'high' as const,
        category: e.category,
        ticker: e.ticker,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [calendarEvents, now]);

  const closestEvent = nearbyEvents[0] ?? null;

  return {
    nearbyEvents,
    isNearEvent: nearbyEvents.length > 0,
    closestEvent,
    minutesUntilClosest: closestEvent?.minutesUntil ?? null,
  };
}
